import { clsx } from 'clsx';
import { forwardRef, useId } from 'react';
import type { InputHTMLAttributes, ReactNode } from 'react';

type TextFieldProps = {
  label: string;
  hint?: ReactNode;
  error?: string;
  showCounter?: boolean;
  containerClassName?: string;
} & Omit<InputHTMLAttributes<HTMLInputElement>, 'id'>;

export const TextField = forwardRef<HTMLInputElement, TextFieldProps>(function TextField(
  { label, hint, error, showCounter, maxLength, value, containerClassName, className, ...rest },
  ref,
) {
  const generatedId = useId();
  const inputId = `tf-${generatedId}`;
  const hintId = hint ? `${inputId}-hint` : undefined;
  const errorId = error ? `${inputId}-error` : undefined;

  const currentLength = typeof value === 'string' ? value.length : 0;

  return (
    <div className={clsx('flex flex-col gap-2', containerClassName)}>
      <div className="flex items-baseline justify-between gap-3">
        <label
          htmlFor={inputId}
          className="font-mono text-xs uppercase tracking-stamp text-champagne"
        >
          {label}
        </label>
        {showCounter && typeof maxLength === 'number' ? (
          <span
            className={clsx(
              'font-mono text-[10px] tabular-nums',
              currentLength > maxLength * 0.9 ? 'text-error' : 'text-neutral-300',
            )}
          >
            {currentLength}/{maxLength}
          </span>
        ) : null}
      </div>

      <input
        ref={ref}
        id={inputId}
        value={value}
        maxLength={maxLength}
        aria-describedby={[hintId, errorId].filter(Boolean).join(' ') || undefined}
        aria-invalid={error ? true : undefined}
        className={clsx(
          'w-full rounded-md border bg-night/40 px-4 py-3 text-base text-parchment',
          'placeholder:text-neutral-300/50',
          'transition-colors duration-fast',
          'focus:outline-none focus:ring-0 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-gold',
          error
            ? 'border-error/70 focus:border-error'
            : 'border-gold/20 focus:border-gold',
          className,
        )}
        {...rest}
      />

      {hint && !error ? (
        <p id={hintId} className="text-xs italic leading-relaxed text-champagne/60">
          {hint}
        </p>
      ) : null}

      {error ? (
        <p
          id={errorId}
          role="alert"
          aria-live="polite"
          className="flex items-start gap-2 text-xs text-error"
        >
          <svg
            viewBox="0 0 24 24"
            className="mt-0.5 h-3.5 w-3.5 flex-shrink-0"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
            aria-hidden="true"
          >
            <circle cx="12" cy="12" r="9" />
            <path d="M12 8v5" />
            <path d="M12 16h.01" />
          </svg>
          {error}
        </p>
      ) : null}
    </div>
  );
});
